import { useMemo } from "react";
import { Crown } from "lucide-react";
import MetricCard from "../ui/MetricCard";
import {
  resolveTeamMonthSalary,
  resolveTeamMonthBonus,
} from "../../lib/calculations";
import { monthKey, today } from "../../lib/dateUtils";
import { formatUSD } from "../../lib/formatters";

// Three-card snapshot of owner economics for the current year.
//   1. Salary owed YTD  — what the owner SHOULD have been paid (per Team profile)
//   2. Withdrawn YTD    — sum of bank-feed rows matched as "owner_withdrawal"
//   3. Gap              — Withdrawn - Owed
//      positive  → owner drew BEYOND the fixed salary (true owner draw / equity)
//      negative  → owner is owed back-salary
//      zero/near → on plan
//
// Renders nothing if no owner is configured (so this only shows up after the
// user checks "is_owner" on a team member).
export default function OwnerKpiCard({ state }) {
  const team = state.team || [];
  const oneOffs = state.oneOffPayments || [];
  const vacations = state.vacations || [];
  const fxRates = state.fxRates || [];

  const owner = useMemo(() => team.find((m) => m.isOwner === true) || null, [team]);

  // YTD math is keyed off the current month. "Through this month" = months
  // 01..currentMonth inclusive. Includes the running month so partial
  // accrual is visible.
  const { owedYtd, withdrawnYtd, gap, currentMonthOwed, currentMonthWithdrawn } = useMemo(() => {
    if (!owner) return { owedYtd: 0, withdrawnYtd: 0, gap: 0, currentMonthOwed: 0, currentMonthWithdrawn: 0 };
    const t = today();
    const year = t.getFullYear();
    const currentMonthIdx = t.getMonth();
    const currentMKey = `${year}-${String(currentMonthIdx + 1).padStart(2, "0")}`;

    let owed = 0;
    let curOwed = 0;
    for (let i = 0; i <= currentMonthIdx; i++) {
      const mKey = `${year}-${String(i + 1).padStart(2, "0")}`;
      const monthOwed = resolveTeamMonthSalary(owner, mKey, vacations, fxRates) + resolveTeamMonthBonus(owner, mKey);
      owed += monthOwed;
      if (mKey === currentMKey) curOwed = monthOwed;
    }

    let withdrawn = 0;
    let curWithdrawn = 0;
    for (const o of oneOffs) {
      if (o.matchedSourceType !== "owner_withdrawal") continue;
      if (!o.date) continue;
      if (Number(o.date.slice(0, 4)) !== year) continue;
      const sign = (o.direction || "out") === "out" ? 1 : -1;
      const amt = sign * (Number(o.amount) || 0);
      withdrawn += amt;
      if (monthKey(o.date) === currentMKey) curWithdrawn += amt;
    }

    return {
      owedYtd: owed,
      withdrawnYtd: withdrawn,
      gap: withdrawn - owed,
      currentMonthOwed: curOwed,
      currentMonthWithdrawn: curWithdrawn,
    };
  }, [owner, oneOffs, vacations, fxRates]);

  if (!owner) return null;

  const gapPositive = gap > 0.5;
  const gapNegative = gap < -0.5;
  const gapLabel = gapPositive
    ? "Drawn beyond salary"
    : gapNegative
    ? "Owed back-salary"
    : "On plan";
  const gapAccent = gapPositive ? "red" : gapNegative ? "amber" : "green";

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Crown size={14} className="text-brand-amber" />
        <h3 className="font-display font-semibold text-sm text-brand-ink dark:text-[var(--text-primary)]">
          Owner ({owner.name})
        </h3>
        <span className="text-[11px] text-brand-slate">
          YTD — current month: {formatUSD(currentMonthWithdrawn)} drawn vs {formatUSD(currentMonthOwed)} owed
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <MetricCard
          label="Salary owed YTD"
          value={formatUSD(owedYtd)}
          accent="default"
          sublabel="what should have been paid"
        />
        <MetricCard
          label="Withdrawn YTD"
          value={formatUSD(withdrawnYtd)}
          accent="orange"
          sublabel="matched as owner withdrawal"
        />
        <MetricCard
          label="Gap (draw vs salary)"
          value={formatUSD(Math.abs(gap))}
          accent={gapAccent}
          sublabel={gapLabel}
        />
      </div>
    </div>
  );
}
